import preferenceSlice from "@stores/preference/slices";
import { Dialog, cn, delay } from "kz-ui-sdk";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import styles from "./DailyRewardModal.module.css";
import { DailyRewardModalContent } from "./components/DailyRewardModalContent";

interface DailyRewardModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Callback when modal closes after successful claim
   * Use this to re-trigger name collection or other post-claim actions
   */
  onClaimComplete?: () => void;
  /**
   * Callback when claim fails with an error
   * Use this to refetch promotion status and handle error state
   */
  onClaimError?: () => void;
}

const COME_BACK_TOMORROW_DELAY_MS = 1500;
// Delay after modal closes to wait for transition animation to complete
const MODAL_CLOSE_TRANSITION_DELAY_MS = 300;

export function DailyRewardModal({ isOpen, onClose, onClaimComplete, onClaimError }: DailyRewardModalProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [showComeBackTomorrow, setShowComeBackTomorrow] = useState(false);
  const [isContentFadingOut, setIsContentFadingOut] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hasError, setHasError] = useState(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reset state when modal opens and lock balance updates
  useEffect(() => {
    if (isOpen) {
      setShowComeBackTomorrow(false);
      setIsContentFadingOut(false);
      setHasError(false);
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      // Lock balance updates to prevent immediate WebSocket updates during auto-claim
      dispatch(preferenceSlice.actions.setBalanceWsLocked(true));
    } else {
      // Unlock balance updates when modal closes
      dispatch(preferenceSlice.actions.setBalanceWsLocked(false));
      dispatch(preferenceSlice.actions.setHasPendingBalanceUpdate(false));
    }
  }, [isOpen, dispatch]);

  // Cleanup timeout and unlock balance on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      // Ensure balance is unlocked on unmount
      dispatch(preferenceSlice.actions.setBalanceWsLocked(false));
      dispatch(preferenceSlice.actions.setHasPendingBalanceUpdate(false));
    };
  }, [dispatch]);

  const handleClaimComplete = () => {
    // Start fade-out animation for content
    setIsContentFadingOut(true);

    // Show "Come back tomorrow" after fade-out completes
    delay(400).then(() => {
      setShowComeBackTomorrow(true);

      // Close modal after delay
      closeTimeoutRef.current = setTimeout(() => {
        onClose();
        // Wait for modal transition to complete before calling onClaimComplete callback
        // This ensures the modal is fully closed before triggering post-login info form
        setTimeout(() => {
          onClaimComplete?.();
        }, MODAL_CLOSE_TRANSITION_DELAY_MS);
      }, COME_BACK_TOMORROW_DELAY_MS);
    });
  };

  // Handle claim error - allow immediate closing
  const handleClaimError = () => {
    // Clear any pending close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    // Set error state to allow closing (in case user tries to close via backdrop/ESC)
    setHasError(true);
    // Call the error callback (handler will close modal and refetch)
    onClaimError?.();
  };

  // Prevent closing until after claim is complete or error occurs
  // Instead, trigger animation to hint player to claim first
  const handleCloseAttempt = () => {
    // Allow closing if error occurred or claim is complete
    if (hasError || showComeBackTomorrow) {
      onClose();
      return;
    }
    // Trigger jumping/scaling animation to hint player to claim first
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }
    setIsAnimating(true);
    animationTimeoutRef.current = setTimeout(() => {
      setIsAnimating(false);
      animationTimeoutRef.current = null;
    }, 600); // Animation duration
  };

  // Cleanup animation timeout
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
    };
  }, []);

  return (
    <Dialog
      open={isOpen}
      onClose={handleCloseAttempt}
      title=""
      classes={{
        "container&": cn(
          "relative z-[50] max-h-[calc(100dvh-32px)] flex flex-col !bg-body pt-0 pb-0 px-0 rounded-[24px] border border-stroke-base shadow-none",
          {
            [styles["daily-login-modal-animating"]]: isAnimating,
          },
        ),
        "containerWrapper&": "!z-[10]",
        "backDrop&": cn(`!z-[9] !backdrop-blur-sm`),
      }}
      transition
    >
      <div
        data-daily-reward-modal="true"
        className="relative -mt-[10px] flex min-h-[392px] w-full flex-col"
      >
        <DailyRewardModalContent
          onClaimComplete={handleClaimComplete}
          onClaimError={handleClaimError}
          showComeBackTomorrow={showComeBackTomorrow}
          isContentFadingOut={isContentFadingOut}
        />
      </div>
    </Dialog>
  );
}
